import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { emailify, phoneify, rutify } from '../apiUtils'
import { IRequest, RequestStatus } from './RequestsServices'
import { RequestedChange } from './ChangesServices'
import { IEmbarcacion } from './EmbarcacionesServices'
import { ITripulante } from './TripulantesServices'
import tokenManager from '../../managers/identity/TokenManager'
import { IArmador } from './ArmadoresServices'
import { IMarca } from './MarcasServices'
import { ICobro } from './CobrosServices'
import { IAOZStatusInfo } from './AOZServices'

export type TrabajadorCargos = 'patron' | 'tripulante'

export enum ViajeMuelle {
  ESTE = 'Muelle Este',
  WESTE = 'Muelle Weste',
}

export enum ViajeEstado {
  SALIDA = 'SALIDA',
  CIERRE = 'CIERRE',
  TIMEOUT = 'TIMEOUT',
  CANCELADO = 'CANCEL',
  ANULADO = 'ANULADO',
}

export const ViajePendiente = (viaje: IViaje): boolean => {
  return viaje.estado === null
}

export const ViajeCancelado = (viaje: IViaje): boolean => {
  return viaje.estado === ViajeEstado.CANCELADO || viaje.estado === ViajeEstado.ANULADO
}

export const ViajePasado = (viaje: IViaje): boolean => {
  return viaje.estado === ViajeEstado.CIERRE || viaje.estado === ViajeEstado.SALIDA
}

export const ViajeVencido = (viaje: IViaje): boolean => {
  return viaje.estado === ViajeEstado.TIMEOUT
}

export const ViajeReservado = (viaje: IViaje): boolean => {
  const postDate = new Date().getTime() + 1000 * 60 * 60 * 10
  return new Date(viaje.fecha_viaje).getTime() > postDate
}

export const ViajeAprobado = (viaje: OperatedViaje): boolean => {
  return viaje.asociationData.length > 0 && viaje.asociationData.some((req) => req.estado === RequestStatus.APPROVED)
}

export const PatronAprobadoEnViaje = (viaje: OperatedViaje): boolean => {
  return viaje.asociationData.some(
    (req) =>
      req.estado == RequestStatus.APPROVED &&
      (req.tipo_objeto_2 === 'tripulante' || req.tipo_objeto_2 === 'armador') &&
      req.id_objeto_2!.toString() == viaje.patronData!.id.toString(),
  )
}

export interface IViaje {
  id: number
  embarcacion_id: number
  fecha_viaje: string
  fecha_creacion: string
  fecha_actualizacion: string
  usuario_creacion: number
  activo: boolean
  muelle: ViajeMuelle
  id_armador: number
  marca_fecha_inicio: string | null
  marca_fecha_fin: string | null
  marca_salida: string | null
  autorizado: RequestStatus
  estado: ViajeEstado | null
}

export interface requestedTripulante extends ITripulante {
  requestData: IRequest | undefined
}

export interface requestedArmador extends IArmador {
  requestData: IRequest | undefined
}

export interface OperatedViaje extends IViaje {
  requestData: IRequest | undefined
  posicionamientosData: Array<IRequest>
  asociationData: Array<IRequest>
  embarcacionData: IEmbarcacion | undefined
  patronData: requestedTripulante | undefined
  tripulantesData: Array<requestedTripulante>
  armadorData: IRequest | undefined
  permisoMuelleWesteData: IRequest | undefined
  aozData?: IAOZStatusInfo
  marcasData?: Array<IMarca>
  cobroAplicado?: ICobro | undefined
  cobroRequestData?: IRequest | undefined
}

export const defaultIViaje: IViaje = {
  id: 0,
  embarcacion_id: 0,
  fecha_viaje: '',
  fecha_creacion: '',
  fecha_actualizacion: '',
  usuario_creacion: 0,
  activo: false,
  muelle: ViajeMuelle.ESTE,
  id_armador: 0,
  marca_fecha_inicio: '',
  marca_fecha_fin: '',
  marca_salida: null,
  autorizado: 0,
  estado: null,
}

export default class ViajesServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para tripulantes
    this.bucketID = dataStorageManager.registerBucket<IViaje>(
      this.getAllViajes.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.VIAJES, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertViaje',
            {
              operation: this.insertViaje.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateViaje',
            {
              operation: this.updateViaje.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'cloneViaje',
            {
              operation: this.cloneViaje.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateFechaViaje',
            {
              operation: this.updateFechaViaje.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllViajes(): Promise<IViaje[]> {
    try {
      const response = await dataQueryManager.addQuery<IViaje | IViaje[]>({
        query: `${import.meta.env.VITE_BASE_URL}/viajes/1/op_select_viajes`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data.map((viaje) => ({
          ...viaje,
          estado:
            typeof (viaje.estado as any) === 'object' || viaje.estado === null || viaje.estado.includes('@nil')
              ? null
              : (viaje.estado as any),
          marca_fecha_inicio: viaje.marca_fecha_inicio
            ? new Date(viaje.marca_fecha_inicio).getFullYear() <= 1980
              ? null
              : viaje.marca_fecha_inicio
            : null,
          marca_fecha_fin: viaje.marca_fecha_fin
            ? new Date(viaje.marca_fecha_fin).getFullYear() <= 1980
              ? null
              : viaje.marca_fecha_fin
            : null,
          marca_salida: viaje.marca_salida
            ? new Date(viaje.marca_salida).getFullYear() <= 1980
              ? null
              : viaje.marca_salida
            : null,
        }))
      } else if (response.data) {
        return [response.data].map((viaje) => ({
          ...viaje,
          estado:
            typeof (viaje.estado as any) === 'object' || viaje.estado === null || viaje.estado.includes('@nil')
              ? null
              : (viaje.estado as any),
        }))
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
    return []
  }

  async insertViaje(
    viajeData: Omit<
      IViaje,
      | 'id'
      | 'fecha_creacion'
      | 'fecha_actualizacion'
      | 'activo'
      | 'marca_fecha_inicio'
      | 'marca_fecha_fin'
      | 'marca_salida'
      | 'estado'
    >,
    tripulanteList: number[],
  ): Promise<IViaje | null> {
    try {
      const token = await tokenManager.getToken()
      const time = new Date().toISOString().split('T')[1]
      const body = {
        op_insert_viaje_tripulante: {
          p_embarcacion_id: viajeData.embarcacion_id,
          p_fecha_viaje: viajeData.fecha_viaje.split('T')[0] + 'T' + time,
          p_muelle: viajeData.muelle,
          p_id_armador: viajeData.id_armador,
          p_usuario_creacion: viajeData.usuario_creacion,
          //p_autorizado: viajeData.autorizado,
          p_tripulantes: tripulanteList.join('-'),
        },
      }

      const response = await fetch(`${import.meta.env.VITE_BASE_URL}/viajes/1/op_insert_viaje_tripulante`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      })

      const data = await response.json()

      if (data && 'Entries' in data && 'Entry' in data.Entries) {
        return data.Entries.Entry
      }
      return null
    } catch (error) {
      console.error('Error inserting embarcacion:', error)
    }
    return null
  }

  async updateViaje(viajeData: IViaje, tripulanteList: number[]): Promise<boolean> {
    try {
      const body = {
        op_update_viaje_tripulante: {
          p_id: viajeData.id,
          p_fecha_viaje: viajeData.fecha_viaje,
          p_activo: viajeData.activo,
          p_usuario_creacion: viajeData.usuario_creacion,
          p_tripulantes: tripulanteList.join('-'),
          //p_autorizado: viajeData.autorizado,
          p_estado: viajeData.estado,
          p_muelle: viajeData.muelle,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/viajes/1/op_update_viaje_tripulante`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating viaje:', error)
    }
    return false
  }

  async cloneViaje(viajeID: number, usuario_creacion: number): Promise<IViaje | null> {
    try {
      const body = {
        op_clonar_viaje: {
          viaje: viajeID,
          usuario_creacion: usuario_creacion,
        },
      }

      const response = await dataQueryManager.addQuery<IViaje>({
        query: `${import.meta.env.VITE_BASE_URL}/viajes/1/op_clonar_viaje`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'object') {
        return response.data
      }
    } catch (error) {
      console.error('Error cloning viaje:', error)
    }
    return null
  }

  async updateFechaViaje(viajeData: IViaje): Promise<boolean> {
    try {
      const body = {
        op_update_fecha: {
          fecha_viaje: viajeData.fecha_viaje,
          marca_fecha_inicio: viajeData.marca_fecha_inicio,
          marca_fecha_fin: viajeData.marca_fecha_fin,
          marca_salida: viajeData.marca_salida,
          estado: viajeData.estado,
          id: viajeData.id,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/viajes/1/op_update_fecha`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating fecha viaje:', error)
    }
    return false
  }

  async updateAutorizacionViaje(viajeID: number, autorizado: number): Promise<boolean> {
    try {
      const body = {
        op_update_autorizar_viaje: {
          id: viajeID,
          autorizado: autorizado,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/viajes/1/op_update_autorizar_viaje`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating autorizacion viaje:', error)
    }
    return false
  }
}
